import { useCallback } from 'react';

import { Flex } from '@kognitos/lattice';

export interface IBrowserVideoViewProps {
  /** Playable blob URL produced by {@link useReplayVideo}. */
  videoUrl: string;
  /** Forwarded to the `<video>` element so parents can seek programmatically. */
  videoRef?: React.MutableRefObject<HTMLVideoElement | null>;
  /** Called on every `<video>` timeupdate with the current playback position. */
  onTimeUpdate?: (currentTime: number) => void;
  /**
   * Called once the video's total length is known — fires on load, unlike
   * `timeupdate` which waits for the first play or seek.
   */
  onDurationChange?: (duration: number) => void;
  /**
   * Hides the browser's native control bar. Set when the host renders its own
   * controls, so the two do not sit on top of each other.
   */
  hideNativeControls?: boolean;
  /** Called when playback starts, pauses, or reaches the end. */
  onPlayStateChange?: (isPlaying: boolean) => void;
  /** Called once the first frame is decoded and the video can be shown. */
  onReady?: () => void;
  /** Called when the video fails to load or decode. */
  onError?: () => void;
}

/**
 * Presentational wrapper around `<video>` for replaying a finalized
 * browser session.
 *
 * All fetching, polling, and blob-URL lifecycle lives in
 * `useReplayVideo`; this component only renders. The parent
 * ({@link BrowserPlayer}) decides whether to mount this at all based on
 * the hook's `status` — when status !== 'ready', the player falls back
 * to {@link ScreencastViewer}'s terminal state (last frame + "session
 * ended" badge), no error UI here.
 */
export function BrowserVideoView({
  videoUrl,
  videoRef: externalVideoRef,
  onTimeUpdate,
  onDurationChange,
  hideNativeControls = false,
  onPlayStateChange,
  onReady,
  onError,
}: IBrowserVideoViewProps) {
  'use no memo';

  // Let parents seek without forwarding a ref.
  const setVideoNode = useCallback(
    (node: HTMLVideoElement | null) => {
      if (externalVideoRef) externalVideoRef.current = node;
    },
    [externalVideoRef],
  );

  const handleTimeUpdate = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement>) => {
      if (onTimeUpdate) onTimeUpdate(e.currentTarget.currentTime);
    },
    [onTimeUpdate],
  );

  const handleDurationChange = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement>) => {
      const { duration } = e.currentTarget;
      if (onDurationChange && Number.isFinite(duration)) {
        onDurationChange(duration);
      }
    },
    [onDurationChange],
  );

  const handlePlay = useCallback(
    () => onPlayStateChange?.(true),
    [onPlayStateChange],
  );
  const handlePause = useCallback(
    () => onPlayStateChange?.(false),
    [onPlayStateChange],
  );

  return (
    <Flex height="full" width="full" data-testid="browser-video-view">
      <Flex direction="column" flex="1" height="full" className="min-w-0">
        <video
          ref={setVideoNode}
          src={videoUrl}
          controls={!hideNativeControls}
          className="h-full w-full bg-background"
          onTimeUpdate={handleTimeUpdate}
          onDurationChange={handleDurationChange}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handlePause}
          onLoadedData={onReady}
          onError={onError}
          data-testid="browser-video-element"
        />
      </Flex>
    </Flex>
  );
}
